import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';

import { AirBridgeLoader, LOADER_CANVAS } from './AirBridgeLoader';
import { TEXT } from '../theme/tokens';

// FD-290 — the between-pages loader, in the CONTENT AREA (owner request,
// 2026-08-11).
//
// This reverses part of FD-276, which deliberately kept the animated loader to
// the boot window and left per-navigation loading to each page's skeleton rows.
// That reasoning — an overlay would cover skeletons the pages already render —
// was sound and is now moot: the owner prefers the brand loader here, so the
// skeletons are what it covers, on purpose.
//
// **It is driven by real pending state, not by a timer.** The prototype held for
// a fixed 1600ms because its data was mock and resolved instantly; its own
// comment says a real integration would tie visibility to route data-loading.
// Shipping the timer would tax EVERY navigation 1.6s, including the many that
// are served from cache — worse than the skeletons it replaces. So the hold ends
// when the route's queries settle.
//
// Two bounds keep that honest in both directions:
//
//   MIN_MS — a loader that appears and vanishes in 80ms is a flash, which reads
//            as a glitch rather than as loading. Once shown, it stays briefly.
//   MAX_MS — a query that hangs must not pin the loader forever. Past the cap
//            the page renders and shows its OWN error or empty state, which is
//            far more useful than an animation that never resolves.
//
// It is mounted inside the `relative` <main>, so it covers only the content
// column: the top bar and nav tree never flash, because they are not what
// changed.

/** Minimum visible time once shown, so a cached route does not flash. */
const MIN_MS = 450;

/** Hard cap. A hung query must yield to the page's own error state. */
const MAX_MS = 6000;

/**
 * The hold POLICY, separated from the presentation.
 *
 * Split out because the two are tested differently: this is deterministic
 * timing logic worth asserting precisely, while the overlay's mount/unmount is
 * framer-motion's exit animation — asserting on that in jsdom tests framer, not
 * us, and does it badly (the node stays mounted through the exit, so a
 * fake-timer test reads a settled loader as still visible).
 */
export function useRouteHold(): boolean {
  const location = useLocation();
  const fetching = useIsFetching();
  const [visible, setVisible] = useState(false);

  const previousPath = useRef(location.pathname);
  const shownAt = useRef(0);

  // Start on a real navigation. Keyed off `pathname` alone: a query-param change
  // (a filter toggle, a tab that carries state in the URL) is not a page change,
  // and covering the grid every time someone flips a filter would be hostile.
  useEffect(() => {
    if (previousPath.current === location.pathname) return;
    previousPath.current = location.pathname;
    shownAt.current = Date.now();
    setVisible(true);
  }, [location.pathname]);

  // End when the route's data has settled — respecting both bounds.
  useEffect(() => {
    if (!visible) return;

    const elapsed = Date.now() - shownAt.current;

    if (fetching === 0) {
      const remaining = Math.max(0, MIN_MS - elapsed);
      const timer = setTimeout(() => setVisible(false), remaining);
      return () => clearTimeout(timer);
    }

    const capTimer = setTimeout(() => setVisible(false), Math.max(0, MAX_MS - elapsed));
    return () => clearTimeout(capTimer);
  }, [visible, fetching]);

  return visible;
}

export interface RouteLoaderProps {
  /**
   * Hold the loader up regardless of navigation state (FD-586).
   *
   * Set by `AppShell` while a multi-environment console is still finding out
   * whether the ACTIVE environment will admit this tenant. During that window the
   * routed page is not mounted at all — so no query fires against an environment
   * that is about to refuse — and this is what the operator sees instead. It is
   * the same overlay as a navigation, on purpose: from the operator's side it IS
   * one, the console finding its way to their environment.
   *
   * The bound lives with the probe (`PROBE_SETTLE_TIMEOUT_MS`), not here, because
   * that is where "stop waiting" has to be written for the gate to open.
   */
  hold?: boolean;
}

export default function RouteLoader({ hold = false }: RouteLoaderProps = {}) {
  const navigating = useRouteHold();
  const visible = navigating || hold;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="route-loader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.1 } }}
          exit={{ opacity: 0, transition: { duration: 0.25 } }}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3"
          // Opaque, and the theme canvas: the loader must sit on the same ground as
          // hardcoded to the kit's #F4F2EE, so any other backdrop shows a seam
          // in the resolve frame.
          style={{ backgroundColor: LOADER_CANVAS }}
        >
          <AirBridgeLoader size={128} />
          <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
            Loading…
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
